import React, { useState } from "react";
import { CeckpointStepV2, CehckpointFieldsV2 } from "src/types/checkpoint";
import { Button, Checkbox } from "@rio-cloud/rio-uikit";
import ConfirmDialog from "../Dialogs/Confirm";
import { hhmmToMinutes, minutesToHHMM } from "src/utils/time2minutes";
import { forceHHMM, formatMaskTime } from "@utils";

interface Props {
  steps: CeckpointStepV2[];
  deleteStep: (stepId: number) => void;
  updateStep: React.Dispatch<React.SetStateAction<CeckpointStepV2[]>>;
}

export const StepRender = ({ steps, deleteStep, updateStep }: Props) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [stepToDelete, setStepToDelete] = useState<number | null>(null);

  const handleCheckboxChange = (step_index: number, field_index: number, checked: boolean) => {
    updateStep((prevSteps) => {
      const newSteps = [...prevSteps];
      const step = newSteps[step_index];
      if (step.fields && step.fields[field_index]) {
        // Garante que fields é um array antes de modificar
        const newFields = [...step.fields];
        newFields[field_index] = {
          ...newFields[field_index],
          required: checked,
        };
        step.fields = newFields;
      }
      return newSteps;
    });
  };

  const updateFieldValue = (step_index: number, field_index: number, value: string | number | boolean) => {
    updateStep((prevSteps) => {
      const newSteps = [...prevSteps];
      const step = newSteps[step_index];
      if (step.fields && step.fields[field_index]) {
        // Garante que fields é um array antes de modificar
        const newFields = [...step.fields];
        newFields[field_index] = {
          ...newFields[field_index],
          estimatedValue: value,
        };
        step.fields = newFields;
      }
      return newSteps;
    });
  };

  const renderField = (field: CehckpointFieldsV2, step_index: number, field_index: number) => {
    if (!field) return "N/A";
    switch (field.field?.type) {
      case "NUMBER":
        return (
          <div className="">
            <div className="display-flex flex-row gap-4 align-items-center">
              <Checkbox
                checked={field.required || false}
                onChange={(e) => handleCheckboxChange(step_index, field_index, e.target.checked)}
              ></Checkbox>
              <label form="time" style={{ margin: 0, verticalAlign: "middle" }}>
                {field.field.title}
              </label>
            </div>
            <input
              type="number"
              className="form-control"
              id="time"
              placeholder="Valor estimado"
              value={Number(field.estimatedValue) || ""}
              onChange={(e) => {
                updateFieldValue(step_index, field_index, parseFloat(e.target.value));
              }}
              required={field.required}
            />
          </div>
        );
      case "TEXT":
        return (
          <div>
            <div className="display-flex flex-row gap-4 align-items-center">
              <Checkbox
                checked={field.required || false}
                onChange={(e) => handleCheckboxChange(step_index, field_index, e.target.checked)}
              ></Checkbox>
              <label form="time" style={{ margin: 0, verticalAlign: "middle" }}>
                {field.field.title}
              </label>
            </div>
            <input
              type="text"
              className="form-control"
              id={field.id}
              value={field.estimatedValue == null ? "" : String(field.estimatedValue)}
              onChange={(e) => updateFieldValue(step_index, field_index, e.target.value)}
              required={field.required}
            />
          </div>
        );
      case "DATE":
        return (
          <div>
            <div className="display-flex flex-row gap-4 align-items-center">
              <Checkbox
                checked={field.required || false}
                onChange={(e) => handleCheckboxChange(step_index, field_index, e.target.checked)}
              ></Checkbox>
              <label form="time" style={{ margin: 0, verticalAlign: "middle" }}>
                {field.field.title ?? ""}
              </label>
            </div>
            <input
              type="date"
              className="form-control"
              id={field.id}
              value={String(field.estimatedValue) || ""}
              onChange={(e) => updateFieldValue(step_index, field_index, e.target.value)}
              required={field.required}
            />
          </div>
        );
      case "IMAGE":
        return (
          <div className="display-flex ">
            <Checkbox
              checked={field.required || false}
              onChange={(e) => handleCheckboxChange(step_index, field_index, e.target.checked)}
            >
              {field.field.title}
            </Checkbox>
          </div>
        );
      case "LIST":
        return (
          <div>
            <div className="display-flex flex-row gap-4 align-items-center">
              <Checkbox
                checked={field.required || false}
                onChange={(e) => handleCheckboxChange(step_index, field_index, e.target.checked)}
              ></Checkbox>
              <label form="time" style={{ margin: 0, verticalAlign: "middle" }}>
                {field.field.title}
              </label>
            </div>
            <select
              className="form-control"
              id={field.id}
              value={String(field.estimatedValue) || field.field?.options?.[0] || ""}
              onChange={(e) => updateFieldValue(step_index, field_index, e.target.value)}
              required={field.required}
            >
              {field.field?.options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        );

      default:
        return <></>;
    }
  };

  const handleDeleteStep = (i: number) => {
    setShowConfirmDialog(true);
    setStepToDelete(i);
  };

  return (
    <div className="lead" style={{ padding: "5px" }}>
      <div className="display-flex flex-column gap-10">
        {steps.map((step, step_index) => {
          let estimedTime;
          if (typeof step.estimatedTimeMinutes === "number") {
            estimedTime = minutesToHHMM(parseInt(step.estimatedTimeMinutes));
          } else {
            estimedTime = step.estimatedTimeMinutes;
          }
          return (
            <div
              key={step.id}
              style={{ backgroundColor: "#f9f9f9", borderRadius: "8px" }}
              className="display-flex flex-column gap-5 padding-10 margin-bottom-10"
            >
              <div className="display-flex flex-row justify-content-between align-items-center">
                <div className="text-size-h6 text-bold">{`${step_index + 1}° Etapa`}</div>
                <Button
                  bsStyle={Button.DEFAULT}
                  variant={Button.VARIANT_LINK}
                  iconOnly 
                  iconName='rioglyph rioglyph-trash' 
                  onClick={() => {
                    handleDeleteStep(step_index);
                  }} />
              </div>
              <div
                className="fields-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "10px",
                  margin: 0,
                  padding: 0,
                  listStyle: "none",
                  alignItems: "center",
                }}
              >
                <div>
                  <label>Título da etapa</label>
                  <input
                    type="text"
                    className="form-control"
                    id={step.title}
                    value={step.title}
                    onChange={(e) =>
                      updateStep((prevSteps) => {
                        const newSteps = [...prevSteps];
                        newSteps[step_index].title = e.target.value;
                        return newSteps;
                      })
                    }
                  />
                </div>

                <div>
                  <label>Tempo estimado</label>

                  <input
                    type="text"
                    className="form-control"
                    min="00:00"
                    placeholder="00:00"
                    id={step.estimatedTimeMinutes}
                    value={estimedTime}
                    onChange={(e) =>
                      updateStep((prevSteps) => {
                        const newSteps = [...prevSteps];
                        newSteps[step_index].estimatedTimeMinutes = formatMaskTime(e.target.value);
                        return newSteps;
                      })
                    }
                    onBlur={(event) => {
                      updateStep((prevSteps) => {
                        const newSteps = [...prevSteps];
                        newSteps[step_index].estimatedTimeMinutes = forceHHMM(event.target.value);
                        return newSteps;
                      });
                    }}
                  />
                </div>

                {step.fields &&
                  step.fields.length > 0 &&
                  step.fields.map((field, i) => (
                    <div key={field.id} className="mb-10">
                      {renderField(field, step_index, i)}
                    </div>
                  ))}
              </div>
            </div>
          );
        })}
      </div>
      <ConfirmDialog
        show={showConfirmDialog}
        onClickConfirm={() => {
          setShowConfirmDialog(false);
          deleteStep(stepToDelete!);
        }}
        onClickCancel={() => {
          setShowConfirmDialog(false);
          setStepToDelete(null);
        }}
        title="Excluir Etapa"
        content="Você tem certeza que deseja excluir esta etapa? Esta ação não pode ser desfeita."
        confirmButtonText="Sim, excluir"
        cancelButtonText="Não, manter"
      />
    </div>
  );
};
